import { Listbox, Transition } from '@headlessui/react';
import { ArrowTrendingUpIcon, CheckIcon } from '@heroicons/react/20/solid';
import { FlagIcon, ReceiptRefundIcon } from '@heroicons/react/24/outline';
import { FlagIcon as FlagSolidIcon } from '@heroicons/react/24/solid';
import { Fragment, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

const sportsbooks = [
  { id: 1, name: 'Early CRIS', value: 'early_cris' },
  { id: 2, name: 'Early Pinnacle', value: 'early_pinnacle' },
  { id: 3, name: 'Early BetOnline', value: 'early_betonline' },
  { id: 4, name: 'Early Circa', value: 'early_circa' },
];

const known = sportsbooks.map((item) => item.value);

const FlagDropdown = ({ id, flags = [] }: { id: API.Id; flags?: string[] }) => {
  const unusedFlags = flags.filter((str) => !known.includes(str));

  const updateMessageRequest = useMemo<
    RequestSchema<API.Message, { data: { meta: { flags: string[] } } }>
  >(
    () => ({
      input: `/api/v1/admin/message/update/${id}`,
      method: 'POST',
    }),
    [id],
  );
  const request = useRequest(updateMessageRequest, { manual: true });

  return (
    <Listbox
      disabled={request.loading}
      value={flags}
      onChange={(nextFlags) => {
        request.run({ data: { meta: { flags: nextFlags } } });
      }}
      multiple
    >
      {({ open }) => (
        <div>
          <Listbox.Button className="relative p-2 hover:bg-emerald-500/5">
            {flags?.length > 0 ? (
              <FlagSolidIcon
                className="h-4 w-auto shrink-0 text-emerald-500 dark:text-emerald-400"
                aria-hidden="true"
              />
            ) : (
              <FlagIcon
                className="h-4 w-auto shrink-0 text-zinc-600 dark:text-zinc-400"
                aria-hidden="true"
              />
            )}
          </Listbox.Button>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute right-0 z-10 mt-1 max-h-60 w-auto overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-zinc-900/5 focus:outline-none dark:bg-zinc-900 dark:ring-white/5 sm:text-sm">
              {sportsbooks.map((sportsbook) => (
                <Listbox.Option
                  key={sportsbook.id}
                  className={({ active }) =>
                    twMerge(
                      active ? 'bg-emerald-500/80 text-white' : 'text-zinc-900 dark:text-zinc-200',
                      'group/option relative cursor-default select-none py-2 pl-3 pr-9',
                    )
                  }
                  value={sportsbook.value}
                >
                  {({ selected, active }) => (
                    <>
                      <span className="flex flex-row items-center space-x-2">
                        <ArrowTrendingUpIcon
                          className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                          aria-hidden="true"
                        />
                        <span
                          className={twMerge(
                            selected ? 'font-semibold' : 'font-normal',
                            'min-w-20 max-w-32 block truncate',
                          )}
                        >
                          {sportsbook.name}
                        </span>
                      </span>

                      {selected ? (
                        <span
                          className={twMerge(
                            active ? 'text-white' : 'text-emerald-500',
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                          )}
                        >
                          <CheckIcon className="h-5 w-auto shrink-0" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}

              {unusedFlags.map((flag) => (
                <Listbox.Option
                  key={flag}
                  className={({ active }) =>
                    twMerge(
                      active ? 'bg-emerald-500/80 text-white' : 'text-zinc-900 dark:text-zinc-200',
                      'group/option relative cursor-default select-none py-2 pl-3 pr-9',
                    )
                  }
                  value={flag}
                >
                  {({ selected, active }) => (
                    <>
                      <span className="flex flex-row items-center space-x-2">
                        {flag.startsWith('early') ? (
                          <ArrowTrendingUpIcon
                            className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                            aria-hidden="true"
                          />
                        ) : flag.startsWith('otb') ? (
                          <ReceiptRefundIcon
                            className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                            aria-hidden="true"
                          />
                        ) : (
                          <FlagIcon
                            className="h-4 w-auto shrink-0 text-zinc-400 group-hover/option:text-emerald-100 group-active/option:text-emerald-100 dark:text-zinc-600"
                            aria-hidden="true"
                          />
                        )}
                        <span
                          className={twMerge(
                            selected ? 'font-semibold' : 'font-normal',
                            'min-w-20 max-w-32 block truncate',
                          )}
                        >
                          {flag}
                        </span>
                      </span>

                      {selected ? (
                        <span
                          className={twMerge(
                            active ? 'text-white' : 'text-emerald-500',
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                          )}
                        >
                          <CheckIcon className="h-5 w-auto shrink-0" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}

              <Listbox.Option
                className="group/option pointer-events-none relative cursor-default select-none p-0 text-zinc-900 dark:text-zinc-200"
                value={null}
              >
                <form
                  className="pointer-events-auto flex w-full space-x-1 px-1 pt-1"
                  action="/release-center"
                  method="POST"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                  }}
                  onSubmit={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (request.loading) {
                      return;
                    }

                    const target = event.target as HTMLFormElement;
                    const elements = target.elements;
                    const json = [...elements].reduce(
                      (data, element) => {
                        const el = element as HTMLInputElement;

                        if (el.name && el.value?.trim?.()) {
                          const next = data ? data : {};

                          next[el.name] = el.value.trim().toLowerCase().replace(/ /g, '_');

                          return next;
                        }

                        return data;
                      },
                      null as Record<string, string> | null,
                    );

                    if (json?.name) {
                      await request.run({ data: { meta: { flags: [...flags, json.name] } } });
                      target.reset();
                    }
                  }}
                >
                  <input
                    className="m-0 w-32 max-w-full flex-1 shrink border-none bg-white/10 py-2 pl-3 pr-9 text-base sm:text-sm"
                    name="name"
                    placeholder="Custom"
                    autoComplete="off"
                    aria-autocomplete="none"
                  />
                  <button
                    type="submit"
                    className="flex-1 rounded border-0 bg-emerald-500/20 px-3 py-1 text-sm text-white hover:bg-emerald-600"
                  >
                    <span
                      className={twMerge(
                        'inline-block h-4 w-4 leading-4',
                        request.loading && 'animate-pulse animate-spin',
                      )}
                    >
                      +
                    </span>
                  </button>
                </form>
              </Listbox.Option>
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};

export default FlagDropdown;
